'use client';

import { useParams } from 'next/navigation';
import Navigation from '@/components/Navigation';
import { MOCK_LISTINGS, type Listing } from '@/app/lib/mock-db';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Calendar, MapPin, Gauge, Fuel, ShieldCheck, Share2, Heart,
  Info, History, Car, Sparkles, TrendingUp, Target, HandCoins,
  GaugeCircle, Loader2, CheckCircle2, AlertCircle, Send, MessageSquareText
} from 'lucide-react';
import { useCountry } from '@/context/CountryContext';
import ContactButtons from '@/components/ContactButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useState, useMemo, useEffect } from 'react';
import { getSimilarMarketStats, computeDealScore, computeNegotiation, computeFitAdvice } from '@/lib/market-insights';
import { callAiFlow } from '@/lib/ai-client';
import type { CarExpertChatOutput } from '@/ai/flows/car-expert-chat';
import { useBehaviorTracker } from '@/hooks/useBehaviorTracker';
import { fetchCarListingById } from '@/lib/car-listings';
import { useToast } from '@/hooks/use-toast';
import { getStoredAccessToken, normalizeRole, readStoredUser } from '@/lib/auth/session';

export default function ListingDetailsPage() {
  const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
  const params = useParams();
  const id = Array.isArray(params.id) ? params.id[0] : params.id;
  const { selectedCountry } = useCountry();
  const { toast } = useToast();
  const fallbackListing = useMemo<Listing>(
    () => MOCK_LISTINGS.find((l) => l.id === id) || MOCK_LISTINGS[0],
    [id]
  );
  const [listing, setListing] = useState<Listing>(fallbackListing);

  const [userPurpose, setUserPurpose] = useState<string>('city');
  const [userDistance, setUserDistance] = useState<string>('short');
  const [userPriority, setUserPriority] = useState<string>('savings');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // AI Chat State
  const [chatQuery, setChatQuery] = useState('');
  const [chatResponse, setChatResponse] = useState<string | null>(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [requestPrice, setRequestPrice] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [purchaseSending, setPurchaseSending] = useState(false);
  const [purchaseSaved, setPurchaseSaved] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [sessionRole, setSessionRole] = useState<string>('guest');

  // حساب الإحصائيات الأساسية
  const stats = useMemo(() => getSimilarMarketStats(listing), [listing]);
  const deal = useMemo(() => computeDealScore(listing.price, stats.avgPrice), [listing.price, stats.avgPrice]);
  const negotiation = useMemo(() => computeNegotiation(listing.price, stats.avgPrice, listing.created_at), [listing, stats]);

  // منطق تحليل الملاءمة التفاعلي
  const [fit, setFit] = useState<any>(null);

  // User Behavior Tracking
  const { logActivity } = useBehaviorTracker();

  useEffect(() => {
    setListing(fallbackListing);
  }, [fallbackListing]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    fetchCarListingById(String(id)).then((row) => {
      if (alive && row) setListing(row);
    });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    if (listing) {
      logActivity('viewed_car', listing.id, listing.title, {
        price: listing.price,
        year: listing.year,
        make: 'Hyundai' // Simplified for demo
      });
    }
  }, [listing?.id]);

  useEffect(() => {
    setIsAnalyzing(true);
    const timer = setTimeout(() => {
      const result = computeFitAdvice(listing, userPurpose, userDistance, userPriority);
      setFit(result);
      setIsAnalyzing(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [userPurpose, userDistance, userPriority, listing]);

  useEffect(() => {
    const syncSession = () => {
      const token = getStoredAccessToken();
      const user = readStoredUser();
      setSessionToken(token);
      setSessionRole(normalizeRole(user?.role as string | undefined));
    };
    syncSession();
    window.addEventListener('storage', syncSession);
    window.addEventListener('s360-auth-changed', syncSession);
    return () => {
      window.removeEventListener('storage', syncSession);
      window.removeEventListener('s360-auth-changed', syncSession);
    };
  }, []);

  const canRequestPurchase = Boolean(sessionToken) && !['admin', 'superadmin'].includes(sessionRole);

  const handleCreatePurchaseRequest = async () => {
    if (!sessionToken) {
      toast({
        variant: 'destructive',
        title: 'تسجيل الدخول مطلوب',
        description: 'يرجى تسجيل الدخول أولاً لإرسال طلب شراء.',
      });
      return;
    }
    setPurchaseSending(true);
    try {
      const payload: Record<string, unknown> = {
        message: requestMessage.trim(),
      };
      if (requestPrice.trim()) payload.offered_price = Number(requestPrice);

      const res = await fetch(`${API}/api/marketplace/listings/${listing.id}/purchase-requests/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || 'تعذر إرسال الطلب حالياً.');
      }
      setPurchaseSaved(true);
      setRequestMessage('');
      setRequestPrice('');
      toast({
        title: 'تم إرسال طلب الشراء',
        description: 'يمكنك متابعة حالة الطلب من صفحة الملف الشخصي.',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'تعذر إرسال الطلب',
        description: error?.message || 'حدث خطأ غير متوقع، حاول لاحقاً.',
      });
    } finally {
      setPurchaseSending(false);
    }
  };

  const handleAskExpert = async () => {
    if (!chatQuery.trim()) return;
    setIsChatLoading(true);
    setChatResponse(null);
    try {
      const result = await callAiFlow<
        { userQuery: string; carDetails: { title: string; price: number; year: number; description: string } },
        CarExpertChatOutput
      >('car-expert-chat', {
        userQuery: chatQuery,
        carDetails: {
          title: listing.title,
          price: listing.price,
          year: listing.year,
          description: listing.description
        }
      });
      setChatResponse(result.answer);
      setChatQuery('');
    } catch (error) {
      console.error(error);
      setChatResponse("عذراً، حدث خطأ أثناء معالجة سؤالك. حاول مرة أخرى.");
    } finally {
      setIsChatLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation title="تفاصيل السيارة" />

      <div className="max-w-6xl mx-auto p-4 space-y-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-none shadow-sm rounded-[3rem] overflow-hidden bg-white">
              <div className="relative aspect-video w-full bg-slate-100">
                <img src={listing.image_url} alt={listing.title} className="w-full h-full object-cover" />
                <div className="absolute top-6 left-6 flex gap-2">
                  <Badge className="bg-white/80 backdrop-blur-md text-slate-800 hover:bg-white p-2 rounded-full cursor-pointer">
                    <Heart className="h-5 w-5" />
                  </Badge>
                  <Badge className="bg-white/80 backdrop-blur-md text-slate-800 hover:bg-white p-2 rounded-full cursor-pointer">
                    <Share2 className="h-5 w-5" />
                  </Badge>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h1 className="text-3xl font-black font-headline mb-2">{listing.title}</h1>
                    <div className="flex gap-4 text-slate-400 font-bold text-xs">
                      <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-secondary" /> {selectedCountry?.name || 'الرياض'}</span>
                      <span className="flex items-center gap-1"><History className="h-4 w-4 text-secondary" /> تم النشر: قبل ساعتين</span>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="text-3xl font-black text-primary">{listing.price.toLocaleString()} <span className="text-lg">{selectedCountry?.currency}</span></p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-slate-50 rounded-[2.5rem]">
                  <InfoBox icon={Calendar} label="سنة الصنع" value={listing.year.toString()} />
                  <InfoBox icon={Gauge} label="الممشى" value="15,000 كم" />
                  <InfoBox icon={Fuel} label="الوقود" value="بنزين" />
                  <InfoBox icon={Car} label="الحالة" value="ممتازة" />
                </div>
              </CardContent>
            </Card>

            {/* Smart Buy Insights Section */}
            <Card className="border-none shadow-md rounded-[2.5rem] bg-white p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-primary/10 rounded-xl">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-black font-headline">📊 نصائح ذكية قبل الشراء</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Deal Score */}
                <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 flex gap-4">
                  <div className="h-12 w-12 bg-white rounded-2xl flex items-center justify-center shadow-sm shrink-0">
                    <GaugeCircle className={`h-6 w-6 ${deal.score >= 7 ? 'text-green-500' : 'text-orange-500'}`} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">درجة الصفقة</p>
                    <h4 className="text-lg font-black">{deal.score}/10</h4>
                    <p className="text-xs text-slate-500 font-medium">{deal.message}</p>
                  </div>
                </div>

                {/* Negotiation */}
                <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 flex gap-4">
                  <div className="h-12 w-12 bg-white rounded-2xl flex items-center justify-center shadow-sm shrink-0">
                    <HandCoins className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">مؤشر التفاوض</p>
                    <h4 className="text-lg font-black">{negotiation.level}</h4>
                    <p className="text-xs text-slate-500 font-medium">{negotiation.suggestion}</p>
                  </div>
                </div>

                {/* Fit for you - Interactive Section */}
                <div className="p-6 bg-indigo-50/30 rounded-[2.5rem] border border-indigo-100 md:col-span-2 space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                      <Target className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="font-black text-indigo-900">المستشار الشخصي: هل تناسبك؟</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase">نوع الاستخدام؟</p>
                      <div className="flex flex-wrap gap-2">
                        {['city', 'travel', 'family'].map((p) => (
                          <button
                            key={p} onClick={() => setUserPurpose(p)}
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all ${userPurpose === p ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 border'}`}
                          >
                            {p === 'city' ? 'داخل المدينة' : p === 'travel' ? 'سفر طويل' : 'للعائلة'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase">المسافة اليومية؟</p>
                      <div className="flex flex-wrap gap-2">
                        {['short', 'long'].map((d) => (
                          <button
                            key={d} onClick={() => setUserDistance(d)}
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all ${userDistance === d ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 border'}`}
                          >
                            {d === 'short' ? 'أقل من 50كم' : 'أكثر من 50كم'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase">الأولوية القصوى؟</p>
                      <div className="flex flex-wrap gap-2">
                        {['savings', 'comfort', 'power'].map((pr) => (
                          <button
                            key={pr} onClick={() => setUserPriority(pr)}
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all ${userPriority === pr ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 border'}`}
                          >
                            {pr === 'savings' ? 'توفير' : pr === 'comfort' ? 'راحة' : 'قوة'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="relative mt-4 p-5 bg-white rounded-3xl border border-indigo-50 shadow-sm min-h-[100px] flex items-center justify-center overflow-hidden">
                    {isAnalyzing ? (
                      <div className="flex flex-col items-center gap-2 animate-in fade-in duration-300">
                        <Loader2 className="h-6 w-6 text-indigo-600 animate-spin" />
                        <p className="text-[10px] font-bold text-slate-400 animate-pulse">جاري تحليل الخيارات...</p>
                      </div>
                    ) : (
                      <div className="w-full space-y-3 animate-in slide-in-from-bottom-2 duration-500">
                        <div className="flex items-center gap-2 text-emerald-600">
                          <CheckCircle2 className="h-4 w-4" />
                          <p className="text-sm font-black">النتيجة: {fit?.status}</p>
                        </div>
                        <p className="text-sm text-slate-600 font-medium leading-relaxed">{fit?.message}</p>
                        <div className="flex flex-wrap gap-2">
                          {fit?.strengths.map((s: string, i: number) => (
                            <Badge key={i} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-100 text-[9px] font-bold px-3">{s}</Badge>
                          ))}
                          {fit?.warnings.map((w: string, i: number) => (
                            <Badge key={i} variant="outline" className="bg-orange-50 text-orange-700 border-orange-100 text-[9px] font-bold px-3 flex gap-1"><AlertCircle className="h-3 w-3" /> {w}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Ask the Expert Chat Area */}
                  <div className="mt-8 pt-8 border-t border-indigo-100 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-primary text-white rounded-xl flex items-center justify-center shadow-md">
                        <MessageSquareText className="h-5 w-5" />
                      </div>
                      <h4 className="font-black text-slate-800">اسأل الخبير حول هذه السيارة</h4>
                    </div>

                    <div className="flex gap-2">
                      <Input
                        placeholder="هل قطع غيارها متوفرة؟ هل تناسب الطرق الوعرة؟"
                        className="rounded-2xl h-12 bg-white border-indigo-50 focus:ring-primary shadow-sm"
                        value={chatQuery}
                        onChange={(e) => setChatQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAskExpert()}
                      />
                      <Button
                        className="h-12 w-12 rounded-2xl bg-primary text-white shadow-lg"
                        onClick={handleAskExpert}
                        disabled={isChatLoading || !chatQuery.trim()}
                      >
                        {isChatLoading ? <Loader2 className="animate-spin h-5 w-5" /> : <Send className="h-5 w-5" />}
                      </Button>
                    </div>

                    {chatResponse && (
                      <div className="p-5 bg-white rounded-[2rem] border border-emerald-100 shadow-sm animate-in slide-in-from-top-2 duration-500">
                        <div className="flex items-center gap-2 text-emerald-600 mb-2">
                          <Sparkles className="h-4 w-4" />
                          <span className="text-[10px] font-black uppercase">رد الخبير الذكي:</span>
                        </div>
                        <p className="text-sm text-slate-700 font-medium leading-relaxed">{chatResponse}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-none shadow-sm rounded-[2.5rem] bg-white p-8">
              <h3 className="text-xl font-black mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-primary" /> وصف السيارة
              </h3>
              <p className="text-slate-600 leading-relaxed font-medium whitespace-pre-wrap">
                {listing.description}
              </p>
            </Card>
          </div>

          {/* Contact & Seller (Right/Sidebar) */}
          <div className="space-y-6">
            <Card className="border-none shadow-xl rounded-[2.5rem] bg-white overflow-hidden p-8 sticky top-28">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-16 w-16 rounded-full bg-slate-100 overflow-hidden border-2 border-primary/10">
                  <img src="https://picsum.photos/seed/seller/100/100" alt="Seller" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="font-black text-lg">معرض النخبة</h4>
                  <p className="text-xs text-slate-400 font-bold">بائع معتمد منذ 2022</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  <span className="text-xs font-bold text-emerald-800">هوية البائع محققة (موثوق)</span>
                </div>

                <div className="pt-4">
                  <ContactButtons
                    phone="0500000000"
                    whatsappPhone="0500000000"
                    chatId="chat_1"
                    message={`أنا مهتم بسيارة ${listing.title} المعلن عنها بسعر ${listing.price} ${selectedCountry?.currency}`}
                  />
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-3">
                  <h5 className="font-bold text-sm">طلب شراء مباشر</h5>
                  {!canRequestPurchase ? (
                    <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 text-[11px] text-slate-600 font-bold">
                      سجّل الدخول بحساب عميل لإرسال طلب شراء ومتابعته من ملفك الشخصي.
                    </div>
                  ) : (
                    <>
                      <Input
                        type="number"
                        className="h-11 rounded-xl"
                        placeholder="السعر المقترح (اختياري)"
                        value={requestPrice}
                        onChange={(e) => setRequestPrice(e.target.value)}
                      />
                      <Textarea
                        className="min-h-[90px] rounded-xl"
                        placeholder="اكتب ملاحظتك للبائع (اختياري)"
                        value={requestMessage}
                        onChange={(e) => setRequestMessage(e.target.value)}
                      />
                      <Button
                        onClick={handleCreatePurchaseRequest}
                        disabled={purchaseSending}
                        className="w-full rounded-xl h-11"
                      >
                        {purchaseSending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'إرسال طلب الشراء'}
                      </Button>
                      {purchaseSaved && (
                        <p className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl p-2">
                          تم حفظ طلبك بنجاح. ستجده ضمن قسم طلبات الشراء في ملفك الشخصي.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="mt-8 pt-8 border-t space-y-4">
                <h5 className="font-bold text-sm">نصائح الأمان</h5>
                <ul className="text-[10px] text-slate-400 space-y-2 font-medium">
                  <li>• لا تقم بتحويل أي مبالغ مالية قبل معاينة السيارة.</li>
                  <li>• اطلب دائماً تقرير فحص السيارة الفني.</li>
                  <li>• المعاينة في مكان عام تضمن سلامتك.</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoBox({ icon: Icon, label, value }: { icon: any, label: string, value: string }) {
  return (
    <div className="flex flex-col items-center text-center space-y-1">
      <div className="p-3 bg-white rounded-2xl shadow-sm text-primary mb-1">
        <Icon className="h-5 w-5" />
      </div>
      <span className="text-[9px] font-bold text-slate-400 uppercase">{label}</span>
      <span className="text-sm font-black text-slate-800">{value}</span>
    </div>
  );
}
